import { useTheme } from '@mui/material';
import MUIButton from '@mui/material/Button';
import { SxProps, Theme } from '@mui/system';
import React from 'react';

interface ButtonProps {
  text?: string;
  children?: React.ReactNode;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (...args: any[]) => any;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium' | 'large';
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
}

const Button = ({
  text,
  variant = 'contained',
  color = 'primary',
  onClick,
  disabled = false,
  fullWidth = false,
  size = 'medium',
  startIcon,
  endIcon,
  style,
  sx,
  children,
}: ButtonProps) => {
  const theme = useTheme();

  const paddingBySize = {
    small: '4px 12px',
    medium: '6px 20px',
    large: '8px 32px',
  };

  const lineHeightBySize = {
    small: '1.4',
    medium: '1.6',
    large: '1.8',
  };

  const colorMap = theme.palette[color];

  const variants = {
    contained: {
      bgColor: theme.palette.primary.main,
      color: colorMap.contrastText,
      fontWeight: 600,
      padding: paddingBySize[size],
      lineHeight: lineHeightBySize[size],
    },
    outlined: {
      bgColor: 'transparent',
      color: colorMap.main,
      border: `1px solid ${colorMap.main}`,
      fontWeight: 600,
      padding: paddingBySize[size],
      lineHeight: lineHeightBySize[size],
    },
    text: {
      bgColor: 'transparent',
      color: colorMap.main,
      border: 'none',
      fontWeight: 600,
      padding: paddingBySize[size],
      lineHeight: lineHeightBySize[size],
    },
  };

  return (
    <MUIButton
      variant={variant}
      color={color}
      onClick={onClick}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      startIcon={startIcon}
      endIcon={endIcon}
      style={{ ...style, borderRadius: '1.5rem' }}
      sx={{ ...variants[variant], ...sx }}
    >
      {text}
      {children}
    </MUIButton>
  );
};

export default Button;
